/**
 * TrainingView - Training Management Module
 * Refactored version using extracted components
 * 
 * Tabs: Send Training | Video Library | Sent History | Certificates | Companies
 */

import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Send, Video, History, Award, Building2 } from 'lucide-react';

// Import extracted components
import {
  TrainingStats,
  SendTrainingWizard,
  VideoLibraryTab,
  TrainingHistoryTab,
  CertificatesTab,
  CompaniesTab
} from './training';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function TrainingView() {
  // Tab state
  const [trainingTab, setTrainingTab] = useState('send');
  
  // Data states
  const [drivers, setDrivers] = useState([]);
  const [trucks, setTrucks] = useState([]);
  const [videos, setVideos] = useState([]);
  const [links, setLinks] = useState([]);
  const [certificates, setCertificates] = useState([]);
  const [companies, setCompanies] = useState([]);
  
  // Loading and refresh states
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(new Date());

  // Fetch all data on mount
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [driversRes, trucksRes, videosRes, linksRes, certsRes, companiesRes] = await Promise.all([
          axios.get(`${API}/fleet/drivers`, { withCredentials: true }),
          axios.get(`${API}/fleet/trucks`, { withCredentials: true }),
          axios.get(`${API}/training/videos`, { withCredentials: true }),
          axios.get(`${API}/training/links`, { withCredentials: true }),
          axios.get(`${API}/training/certificates`, { withCredentials: true }),
          axios.get(`${API}/training/companies`, { withCredentials: true })
        ]);
        setDrivers(driversRes.data?.drivers || driversRes.data || []);
        setTrucks(trucksRes.data?.trucks || trucksRes.data || []);
        setVideos(videosRes.data);
        setLinks(linksRes.data);
        setCertificates(certsRes.data);
        setCompanies(companiesRes.data);
      } catch (e) {
        console.error('Failed to fetch training data:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    
    // Auto-refresh links every 30 seconds
    const interval = setInterval(() => {
      axios.get(`${API}/training/links`, { withCredentials: true })
        .then(res => {
          setLinks(res.data);
          setLastRefresh(new Date());
        })
        .catch(console.error);
    }, 30000);
    
    return () => clearInterval(interval);
  }, []);

  // Refresh history callback
  const refreshHistory = useCallback(async () => {
    try {
      const [linksRes, certsRes] = await Promise.all([
        axios.get(`${API}/training/links`, { withCredentials: true }),
        axios.get(`${API}/training/certificates`, { withCredentials: true })
      ]);
      setLinks(linksRes.data);
      setCertificates(certsRes.data);
      setLastRefresh(new Date());
    } catch (e) {
      console.error('Failed to refresh:', e);
    }
  }, []);

  // Callback for when training is sent
  const onTrainingSent = useCallback(() => {
    refreshHistory();
  }, [refreshHistory]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-pulse text-accent">Loading training data...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="training-view">
      {/* Stats Cards */}
      <TrainingStats 
        videos={videos} 
        links={links} 
        certificates={certificates} 
      />

      {/* Tab Navigation */}
      <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
        <button 
          onClick={() => setTrainingTab('send')} 
          className={`px-4 py-2 rounded-xl font-medium flex items-center gap-2 whitespace-nowrap ${trainingTab === 'send' ? 'bg-accent text-black' : 'bg-card hover:bg-accent/20'}`}
          data-testid="tab-send"
        >
          <Send className="w-4 h-4" />
          Send Training
        </button>
        <button 
          onClick={() => setTrainingTab('library')} 
          className={`px-4 py-2 rounded-xl font-medium flex items-center gap-2 whitespace-nowrap ${trainingTab === 'library' ? 'bg-accent text-black' : 'bg-card hover:bg-accent/20'}`}
          data-testid="tab-library"
        >
          <Video className="w-4 h-4" />
          Video Library ({videos.length})
        </button>
        <button 
          onClick={() => setTrainingTab('history')} 
          className={`px-4 py-2 rounded-xl font-medium flex items-center gap-2 whitespace-nowrap ${trainingTab === 'history' ? 'bg-accent text-black' : 'bg-card hover:bg-accent/20'}`}
          data-testid="tab-history"
        >
          <History className="w-4 h-4" />
          Sent History ({links.length})
        </button>
        <button 
          onClick={() => setTrainingTab('certificates')} 
          className={`px-4 py-2 rounded-xl font-medium flex items-center gap-2 whitespace-nowrap ${trainingTab === 'certificates' ? 'bg-yellow-500 text-black' : 'bg-card hover:bg-yellow-500/20'}`}
          data-testid="tab-certificates"
        >
          <Award className="w-4 h-4" />
          Certificates ({certificates.length})
        </button>
        <button 
          onClick={() => setTrainingTab('companies')} 
          className={`px-4 py-2 rounded-xl font-medium flex items-center gap-2 whitespace-nowrap ${trainingTab === 'companies' ? 'bg-purple-500 text-white' : 'bg-card hover:bg-purple-500/20'}`}
          data-testid="tab-companies"
        >
          <Building2 className="w-4 h-4" />
          Companies ({companies.length})
        </button>
      </div>

      {/* Tab Content */}
      {trainingTab === 'send' && (
        <SendTrainingWizard
          drivers={drivers}
          trucks={trucks}
          videos={videos}
          companies={companies}
          onTrainingSent={onTrainingSent}
        />
      )}

      {trainingTab === 'library' && (
        <VideoLibraryTab 
          videos={videos} 
          setVideos={setVideos} 
        />
      )}

      {trainingTab === 'history' && (
        <TrainingHistoryTab
          links={links}
          refreshHistory={refreshHistory}
          lastRefresh={lastRefresh}
        />
      )}

      {trainingTab === 'certificates' && (
        <CertificatesTab certificates={certificates} />
      )}

      {trainingTab === 'companies' && (
        <CompaniesTab 
          companies={companies} 
          setCompanies={setCompanies} 
        />
      )}
    </div>
  );
}
